import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useExamApi } from "@/api/examApi";
import { createTaskAttempt } from "@/api/exam";
import { supabase } from "../api/supabaseClient";
import "../styles/globle.css";
import "../styles/tips.css";
import { checkSubscriptionAccess } from "@/utils/subscriptionCheck";
import { setExamMode } from "@/utils/examMode";
import { isTask2MaintenanceEnabled } from "@/config/maintenance";
import Task2MaintenanceNotice from "@/components/task2/Task2MaintenanceNotice";

const tasksData = [
  { id: 1, name: "Task 1 — Self introduction" },
  { id: 2, name: "Task 2 — Roleplay questions" },
  { id: 3, name: "Task 3 — Opinion and argument" },
];

const TASK_TITLES: Record<number, string> = {
  1: "Task 1 — Self introduction",
  2: "Task 2 — Roleplay questions",
  3: "Task 3 — Opinion and argument",
};

type Tip = { id: number; created_at: string; task: number; description: string };

function detectTipType(text: string): string {
  const trimmed = text.trim();

  // Big section titles (top line in each accordion)
  if (
    trimmed.startsWith("Speaking time") ||
    trimmed.startsWith("Task 1") ||
    trimmed.startsWith("Task 2") ||
    trimmed.startsWith("Task 3")
  ) {
    return "title";
  }

  // Numbered subsection headings like "1. Opening / basic inquiry"
  if (/^\d+\.\s/.test(trimmed)) {
    return "subtitle";
  }

  // Template headings like "Template 1 — …"
  if (/^Template\s+\d+\s+[—-]/.test(trimmed)) {
    return "subtitle";
  }

  // Example headings like "Example — …", "Example 1 — …", "Example 2 — …"
  if (
    /^Example\s+[—-]/.test(trimmed) ||
    trimmed.startsWith("Example 1 —") ||
    trimmed.startsWith("Example 2 —")
  ) {
    return "subtitle";
  }

  // Subtitles / section headers
  if (
    trimmed === "Objective" ||
    trimmed.startsWith("What examiners expect") ||
    trimmed.startsWith("Examiners look for") ||
    trimmed.startsWith("Typical prompts") ||
    trimmed.startsWith("High-level templates") ||
    trimmed.startsWith("Preparation strategy") ||
    trimmed.startsWith("Question types and templates") ||
    trimmed.startsWith("Example interaction script") ||
    trimmed.startsWith("Useful intro starters for Task 2") ||
    trimmed.startsWith("Useful Intro Starters for Task 2") ||
    trimmed.startsWith("Useful closings for Task 2") ||
    trimmed.startsWith("Useful Closings for Task 2") ||
    trimmed.startsWith("Key phrases") ||
    trimmed.startsWith("Why this structure works") ||
    trimmed.startsWith("Why this works") ||
    trimmed.startsWith("⭐") ||
    // Task 3 specific section headers
    trimmed.startsWith("Proven Argumentative Structure") ||
    trimmed.startsWith("Use this 5-part structure") ||
    trimmed.startsWith("Turn-Key Template") ||
    trimmed.startsWith("General Exam Tips (All Tasks)") ||
    trimmed.startsWith("Fluency and Pronunciation") ||
    trimmed.startsWith("Interactive Skills") ||
    trimmed.startsWith("Time Management") ||
    trimmed.startsWith("Vocabulary and Grammar")
  ) {
    return "subtitle";
  }

  // Bullet lists
  if (trimmed.startsWith("•")) {
    return "list";
  }

  // Template / structured bodies
  if (
    // Task 1 templates
    trimmed.includes("Introduction (~10–15 sec)") ||
    trimmed.includes("Introduction (10–15 sec)") ||
    trimmed.includes("Introduction (10–20 sec)") || // Task 3 timing
    trimmed.startsWith("Opening identity") ||
    trimmed.startsWith("Personal background") ||
    trimmed.startsWith("Professional or academic situation") ||
    trimmed.startsWith("Hobbies and personality") ||
    trimmed.startsWith("Motivation for French and goals") ||
    // Task 3 generic argumentative skeleton (Turn-Key Template body)
    (trimmed.startsWith("Introduction") &&
      trimmed.includes("la question de [sujet] suscite des débats importants")) ||
    // Task 3: the row that starts the whole native template block
    trimmed.startsWith("You can adapt this template to almost any topic")
  ) {
    return "template";
  }

  // Example answers / scripted dialogs
  if (
    // Task 1 + 2 examples
    trimmed.startsWith("Bonjour") ||
    trimmed.startsWith("J’ai grandi") ||
    trimmed.startsWith("Je travaille") ||
    trimmed.startsWith("En dehors du travail") ||
    trimmed.startsWith("Dans mon temps libre") ||
    trimmed.startsWith("Situation:") ||
    trimmed.startsWith("Situation :") ||
    trimmed.startsWith("INTRO") ||
    trimmed.startsWith("QUESTIONS (12 total)") ||
    trimmed.startsWith("ENDING") ||
    // Task 3 native examples
    trimmed.includes("téléphones portables dans la vie des jeunes") ||
    trimmed.includes("l’extension du télétravail dans les entreprises")
  ) {
    return "example";
  }

  // Default: normal paragraph
  return "paragraph";
}

function formatTipDescription(text: string) {
  // 1) Remove leading/trailing blank whitespace lines
  const cleaned = text.replace(/^\s+/, "").replace(/\s+$/, "");

  // 2) Highlight placeholders like [Nom], [Ville/Pays], [Âge]
  const withPlaceholders = cleaned.replace(
    /\[([^\]]+)\]/g,
    '<span class="placeholder">$1</span>'
  );

  // 3) Split section title vs timing
  //    Now supports both "sec" AND "min" so
  //    "Developed arguments (2–3 points, ~2–3 min)" is styled too.
  const withTiming = withPlaceholders.replace(
    /^(.+?)\s*\(([^)]*(sec|min)[^)]*)\)/gm,
    '<span class="template-section-title">$1</span> <span class="template-timing">($2)</span>'
  );

  // 4) Turn bare headings inside the Turn-Key template
  //    into section titles: Introduction / Position / Arguments /
  //    Considération du contre-argument / Conclusion
  const withSectionTitles = withTiming.replace(
    /^(Introduction|Position|Arguments|Considération du contre-argument|Conclusion)\s*$/gm,
    '<span class="template-section-title">$1</span>'
  );

  return withSectionTitles;
}

function getTipWrapperClasses(type: string) {
  switch (type) {
    case "title":
      return "tip-block title";
    case "subtitle":
      return "tip-block subtitle";
    case "list":
      return "tip-block list";
    case "template":
      return "tip-block template";
    case "example":
      return "tip-block example";
    default:
      // covers "paragraph" and anything unknown
      return "tip-block paragraph";
  }
}


function ExploreTemplates() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getExamList } = useExamApi();

  const [selectedTask, setSelectedTask] = useState<number>(
    location.state?.selectedTask
      ? parseInt(location.state.selectedTask.replace("task", ""))
      : 0
  );

  const [selectedTopic, setSelectedTopic] = useState<{
    id: string;
    content: string;
  } | null>(null);

  const [topicsData, setTopicsData] = useState<
    Record<string, { id: string; content: string }[]>
  >({});

  const [loadingTopics, setLoadingTopics] = useState(true);
  const [latestExamId, setLatestExamId] = useState<string | null>(
    location.state?.examId || null
  );

  const [isRetrying, setIsRetrying] = useState(false);
  const disableTopic = location.state?.disableTopic || false;

  const [tipsData, setTipsData] = useState<
    { id: number; created_at: string; task: number; description: string }[]
  >([]);

  const [loadingTips, setLoadingTips] = useState(true);
  const [showTips, setShowTips] = useState(location.state?.showTips || false);
  const [hasSubscriptionAccess, setHasSubscriptionAccess] = useState(true); // Default to true

    // Derived values for tips view
  const taskNumbers = Array.from(new Set(tipsData.map((t) => t.task)))
    .filter((n) => n !== 0) // 0 is reserved for "General exam tips"
    .sort((a, b) => a - b);

  const globalTips = tipsData
    .filter((t) => t.task === 0)
    .slice()
    .sort((a, b) => a.id - b.id);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const checkAccess = async () => {
      const result = await checkSubscriptionAccess();
      setHasSubscriptionAccess(result.hasAccess);
      if (!result.hasAccess) {
        console.log(`Subscription check failed: ${result.reason}`);
      }
    };
    checkAccess();
  }, []);

  useEffect(() => {
    const fetchLatestExam = async () => {
      if (!latestExamId) {
        const res = await getExamList();
        const latestExam = res?.exams?.[0];
        if (latestExam) setLatestExamId(latestExam.id);
      }
    };
    fetchLatestExam();
  }, [getExamList, latestExamId]);

  useEffect(() => {
    const fetchTopics = async () => {
      setLoadingTopics(true);
      setSelectedTopic(null);
      setLoadingTips(true);

      try {
        const response = await fetch(
          `https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/get_tips_examples`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ task_type: selectedTask }),
          }
        );

        if (!response.ok) throw new Error("Failed to fetch topics");
        const data = await response.json();

        setTopicsData(data.topics || {});
        setTipsData(data.tips || []);

        const months = Object.keys(data);
        if (months.length > 0) {
          const firstMonthTopics = data[months[0]];
          if (firstMonthTopics?.length > 0)
            setSelectedTopic(firstMonthTopics[0]);
        }
      } catch {
        setTopicsData({});
      } finally {
        setLoadingTopics(false);
        setLoadingTips(false);
      }
    };

    fetchTopics();
  }, [selectedTask]);

  const handleRetry = async () => {
    // 🔊 UNLOCK AUDIO CONTEXT FOR IOS
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AC) {
        let ctx = (window as any).__appAudioContext;
        if (!ctx) {
          ctx = new AC();
          (window as any).__appAudioContext = ctx;
        }
        
        // Always try to resume
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        
        // Play silent buffer to force unlock
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
        console.log('🔊 Audio context unlocked on Start Task click, state:', ctx.state);
        
        // Short delay to ensure state propagates
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    } catch (e) {
      console.warn('Failed to unlock audio context:', e);
    }

    if (!selectedTask || !latestExamId) {
      alert("Could not determine the task or exam to retry.");
      return;
    }

    if (isTask2MaintenanceEnabled() && selectedTask === 2) {
      return;
    }

    setIsRetrying(true);
    try {
      try {
        const taskString = `task${selectedTask}`;
        await supabase.functions.invoke("delete_transcript", {
          body: { examId: latestExamId, task: taskString },
        });
      } catch { }

      if ((selectedTask === 2 || selectedTask === 3) && selectedTopic) {
        localStorage.setItem("preferred_topic_id", selectedTopic.id);
      }

      const attemptResponse = await createTaskAttempt({
        sessionId: latestExamId,
        taskType: selectedTask,
      });

      if (attemptResponse.success && attemptResponse.taskAttemptId) {
        localStorage.setItem(
          "current_task_attempt_id",
          attemptResponse.taskAttemptId
        );
        localStorage.setItem("current_exam_id", latestExamId);
        localStorage.setItem(
          "current_retry_task_type",
          selectedTask.toString()
        );

        // Launching an Individual Task from templates is Full/paid authority.
        setExamMode("full");
        if (selectedTask === 1)
          navigate("/task-one", { state: { examId: latestExamId } });
        else if (selectedTask === 2)
          navigate("/task-two", { state: { examId: latestExamId } });
        else if (selectedTask === 3)
          navigate("/task-three", { state: { examId: latestExamId } });
      }
    } catch (error: any) {
      alert(`Retry error: ${error.message}`);
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-background ">
      {/* HEADER */}
      <header className="border-b border-neutral-200 bg-card px-[18px] xl:px-0">

        <div className="mx-auto flex max-w-[76rem] items-center gap-4 py-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="h-9 w-9 flex items-center justify-start 
             hover:bg-transparent hover:text-inherit 
             focus-visible:ring-0 active:bg-transparent"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          <div>
            {showTips ? (
              <>
                <h1 className="text-xl font-semibold text-foreground">
                  Tips and examples
                </h1>
                <p className="text-sm text-muted-foreground">
                  Learn key patterns and see sample
                </p>
              </>
            ) : (
              <>
                <h1 className="text-xl font-semibold text-foreground">
                  Topics
                </h1>
                <p className="text-sm text-muted-foreground">
                  Pick a topic and start practicing
                </p>
              </>
            )}
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <div className="mx-auto max-w-[76rem]  pt-4 sm:pt-6 bg-[#F7F9FB]  rounded-2xl px-[18px] xl:px-0">
        <div className="grid gap-6 lg:grid-cols-4">
          {/* LEFT SIDEBAR */}
          <div className="lg:col-span-1">
            <div className="mb-6 flex h-[20px] sm:h-[40px] items-start">
              <h2 className="text-lg font-semibold text-foreground">Tasks</h2>
            </div>

            {/* TASK BUTTONS */}
            <div className="space-y-2">
              {tasksData.map((task) => (
                <button
                  key={task.id}
                  disabled={disableTopic}
                  onClick={() => {
                    setSelectedTask(task.id);
                    setShowTips(false);
                  }}
                  className={`w-full rounded-lg border-[1.5px] px-4 py-3 text-left text-sm font-medium transition-all
                    ${selectedTask === task.id
                      ? "border-blue-600 bg-blue-50 text-[#1C1F26]"
                      : "border-neutral-300 bg-card hover:bg-neutral-50"
                    }
                    ${disableTopic
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                    }`}
                >
                  {task.name}
                </button>
              ))}
            </div>

            {/* SPACING BETWEEN TASKS AND TIPS */}
            <div className="mt-8"></div>

            {/* TIPS BUTTON */}
            <button
              onClick={() => {
                setShowTips(true);
                setSelectedTask(0);
              }}
              className={`w-full rounded-lg border-[1.5px] px-4 py-3 text-left text-sm font-medium transition-all
                ${showTips
                  ? "border-blue-600 bg-blue-50 text-[#1C1F26]"
                  : "border-neutral-300 bg-card hover:bg-neutral-50"
                }`}
            >
              📄 Tips and examples
            </button>
          </div>

          {/* RIGHT CONTENT */}
          <div className="w-full lg:col-span-3">
            {isTask2MaintenanceEnabled() && selectedTask === 2 && !showTips ? (
              <Task2MaintenanceNotice className="mb-6" />
            ) : null}
            {!showTips && (
              <div className="mb-6 flex h-[40px] items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    Select topic
                  </h2>
                  {selectedTopic && (
                    <p className=" max-w-[200px] sm:max-w-md truncate text-sm text-muted-foreground">
                      Selected: {selectedTopic.content}
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleRetry}
                  disabled={
                    !selectedTopic ||
                    isRetrying ||
                    !hasSubscriptionAccess ||
                    (isTask2MaintenanceEnabled() && selectedTask === 2)
                  }
                  size="sm"
                  className=" mt-1 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {isRetrying
                    ? "Starting..."
                    : isTask2MaintenanceEnabled() && selectedTask === 2
                      ? "Temporarily unavailable"
                      : "Start Task"}
                </Button>
              </div>
            )}

            {/* TIPS MODE */}
            {showTips ? (
              <div className="min-h-[500px] w-full pb-20">
                {loadingTips ? (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    Loading tips…
                  </div>
                ) : tipsData.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    No tips found.
                  </div>
                ) : (
                  <Accordion
                    type="multiple"
                    className="w-full space-y-4"
                    defaultValue={["task1"]}
                  >
                    {/* Task-specific sections: Task 1, 2, 3 */}
                    {taskNumbers.map((taskNumber) => {
                      const tipsForTask = tipsData.filter((t) => t.task === taskNumber);
                      if (tipsForTask.length === 0) return null;
                  
                      const title =
                        TASK_TITLES[taskNumber] || `Task ${taskNumber} - Tips and examples`;
                  
                      return (
                        <AccordionItem
                          key={`task${taskNumber}`}
                          value={`task${taskNumber}`}
                          className="w-full rounded-lg border-[1.5px] border-neutral-300 bg-card px-6 shadow-sm"
                        >
                          <AccordionTrigger className="py-5 text-base font-semibold text-blue-700 hover:no-underline">
                            {title}
                          </AccordionTrigger>
                  
                          <AccordionContent className="pb-6 pt-2">
                            <div className="tips-stack">
                              {tipsForTask
                                .slice()
                                .sort((a: Tip, b: Tip) => a.id - b.id)
                                .map((tip) => {
                                  const type = detectTipType(tip.description);
                                  return (
                                    <div key={tip.id} className={getTipWrapperClasses(type)}>
                                      <span
                                        className="tip-text"
                                        dangerouslySetInnerHTML={{
                                          __html: formatTipDescription(tip.description),
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  
                    {/* Global section: General exam tips (task = 0) */}
                    {globalTips.length > 0 && (
                      <AccordionItem
                        key="general-tips"
                        value="general-tips"
                        className="w-full rounded-lg border-[1.5px] border-neutral-300 bg-card px-6 shadow-sm"
                      >
                        <AccordionTrigger className="py-5 text-base font-semibold text-blue-700 hover:no-underline">
                          Overall speaking exam tips
                        </AccordionTrigger>
                  
                        <AccordionContent className="pb-6 pt-2">
                          <div className="tips-stack">
                            {globalTips.map((tip) => {
                              const type = detectTipType(tip.description);
                              return (
                                <div key={tip.id} className={getTipWrapperClasses(type)}>
                                  <span
                                    className="tip-text"
                                    dangerouslySetInnerHTML={{
                                      __html: formatTipDescription(tip.description),
                                    }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )}
                  </Accordion>
                )}
              </div>
            ) : (
              /* TOPICS MODE */
              <div className="min-h-[500px] w-full">
                {loadingTopics ? (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    Loading topics...
                  </div>
                ) : Object.keys(topicsData).length === 0 ? (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    No topics found for this task.
                  </div>
                ) : selectedTask === 1 ? (
                  <div className="w-full space-y-3">
                    {Object.values(topicsData)
                      .flat()
                      .map((topic) => (
                        <button
                          key={topic.id}
                          onClick={() => setSelectedTopic(topic)}
                          className={`w-full rounded-lg border-[1.5px] px-4 py-3 text-left text-sm transition-all
                            ${selectedTopic?.id === topic.id
                              ? "border-blue-600 bg-blue-50 text-[#1C1F26]"
                              : "border-neutral-300 bg-card hover:bg-neutral-50"
                            }`}
                        >
                          {topic.content}
                        </button>
                      ))}
                  </div>
                ) : (
                  <Accordion
                    type="single"
                    collapsible
                    className="w-full space-y-4"
                    defaultValue={Object.keys(topicsData)[0]}
                  >
                    {Object.entries(topicsData).map(([month, topics]) => (
                      <AccordionItem
                        key={month}
                        value={month}
                        className="w-full rounded-lg border-[1.5px] border-neutral-300 bg-card px-6 shadow-sm"
                      >
                        <AccordionTrigger className="py-5 text-base font-semibold text-blue-700 hover:no-underline">
                          {month}
                        </AccordionTrigger>

                        <AccordionContent className="pb-6 pt-2">
                          <div className="space-y-3">
                            {topics.map((topic) => (
                              <button
                                key={topic.id}
                                onClick={() => setSelectedTopic(topic)}
                                className={`w-full rounded-lg border-[1.5px] px-4 py-3 text-left text-sm transition-all
                                  ${selectedTopic?.id === topic.id
                                    ? "border-blue-600 bg-blue-50 text-[#1C1F26]"
                                    : "border-neutral-300 bg-card hover:bg-neutral-50"
                                  }`}
                              >
                                {topic.content}
                              </button>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExploreTemplates;
